import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Store } from "Reducers/interface";
import validatePermissions from "Utils/validatePermissions";
import { MaskCPF } from "Utils/Masks";
import { FormatDate } from "Utils/FormatterDate";
import { CLIENTS } from "Constants/Clients";
import { Button } from "Components/Button";
import { RefreshCcw, Circle } from "lucide-react";
import { Box } from "styles/globalComponents";
import { createToast } from "Utils/toastFunc";
import { ChangeStatusOrder } from "Requests/Orders";
import { SUL_AMERICA_STATUS } from "Constants/Orders";
import { useQueryClient } from "react-query";
import { useState } from "react";
import { Loader } from "Components/Loader";
import Tooltip from "Components/Tooltip";
import { TableItemStyle } from "../styles";
import { getOrderStatusFlag } from "../utils";

export type Order = {
  order_id: number;
  client_id: number;
  client_name: string;
  id_source: number;
  date: string;
  pacient_id: number;
  pacient_name: string;
  pacient_cpf: string;
  pacient_email: string;
  subtotal: string;
  discounts: string;
  total: string;
  paid_value: string;
  coupon_code: boolean;
  home_collected: boolean;
  home_collect_date: string;
  status: string;
  order_source: string;
  origin_order_id: number;
  order_type: number;
  motion_integration_status: string;
  unit_id: number;
  unit_name: string;
  compound_order: boolean;
  deadline_status?: string | null;
};

export interface TableItemProps {
  order: Order;
  onOpenChangeStatus: (orderId: number) => void;
}

export const TableItem = ({ order, onOpenChangeStatus }: TableItemProps) => {
  const [loading, setLoading] = useState(false);
  const user_permissions = useSelector(
    (state: Store) => state.User.permissions
  );

  const queryClient = useQueryClient();

  const {
    date,
    origin_order_id,
    pacient_name,
    pacient_cpf,
    pacient_email,
    home_collect_date,
    paid_value,
    order_id,
    order_type,
    client_name,
    motion_integration_status,
    client_id,
    deadline_status,
  } = order;

  const hasPermission = validatePermissions(
    "Send Orders to Motion",
    user_permissions
  );

  const waitingSendMotion = motion_integration_status === "Aguardando Envio";

  const flagStatus = getOrderStatusFlag(deadline_status);

  const onSubmitOrderToSulAmerica = async () => {
    try {
      if (deadline_status === "SENT") {
        createToast("error", "Pedido já foi enviado para Sul América");
        return;
      }
      setLoading(true);
      await ChangeStatusOrder(order_id, SUL_AMERICA_STATUS.collected);
      await queryClient.invalidateQueries(["client-orders"]);
      createToast("success", "Pedido enviado para Sul América com sucesso");
    } catch (error: any) {
      createToast("error", "Erro ao enviar pedido para Sul América");
    } finally {
      setLoading(false);
    }
  };

  return (
    <TableItemStyle>
      <td>{date.replaceAll("-", "/")} </td>
      <td>{home_collect_date ? FormatDate(home_collect_date) : ""}</td>

      <td>{origin_order_id} </td>

      <td>{pacient_name} </td>

      <td>{MaskCPF(pacient_cpf)} </td>
      <td>{pacient_email} </td>
      <td>{client_name}</td>
      <td>
        {client_id !== CLIENTS.sulamerica && "Não informado"}
        {client_id === CLIENTS.sulamerica ? (
          <>
            {loading ? (
              <div className="loader">
                <Loader size={15} />
              </div>
            ) : (
              <Tooltip text={flagStatus.label}>
                <Button textButton onClick={onSubmitOrderToSulAmerica}>
                  <Circle
                    fill={flagStatus.fill}
                    size={20}
                    color={flagStatus.stroke}
                  />
                </Button>
              </Tooltip>
            )}
          </>
        ) : null}
      </td>
      <td className="table-action-order">
        {waitingSendMotion ? (
          <Link
            type="button"
            className={`button _blue _action 
                ${hasPermission ? "" : "disabled"}
                ${waitingSendMotion ? "" : "disabled"}`}
            to={
              order_type === 5
                ? `/pedido/revisar/${order_id}`
                : `/pedidos-envio/${order_id}`
            }
          >
            Enviar
          </Link>
        ) : (
          motion_integration_status
        )}
      </td>
      <td>R$ {paid_value} </td>
      <td className="table-action-order">
        <Box display="flex" gap="0">
          {client_id === CLIENTS.sulamerica ? (
            <Button
              classButton="button _white _action status-button"
              textButton
              onClick={() => onOpenChangeStatus(order_id)}
            >
              <RefreshCcw />
            </Button>
          ) : (
            <div style={{ marginLeft: "auto" }} />
          )}
          <Link
            to={`/ficha-de-pedido/${order_id}`}
            className="button _white _action"
            type="button"
          >
            <span className="details-text">Visualizar</span>
          </Link>
        </Box>
      </td>
    </TableItemStyle>
  );
};
